import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, CheckCircle2, FolderTree, BarChart3 } from 'lucide-react';

const STEPS = [
  {
    Icon: FileText,
    title: 'Buyer files a request',
    body:
      'The buyer drafts the brief in Orbit: deadline, vertical (Bizop, Refinance, Home Insurance, etc.), assigned editors, reference material. A public upload link is generated automatically.',
    accent: 'from-blue-400 to-blue-600',
  },
  {
    Icon: Upload,
    title: 'Editors deliver',
    body:
      'Each editor uploads through their public link — direct, no login required. Files land in a shared folder under the request, organized by editor and date.',
    accent: 'from-orange-400 to-orange-600',
  },
  {
    Icon: CheckCircle2,
    title: 'Quality Check, two phases',
    body:
      'Reviewers work through the QC queue — Phase 1 catches the obvious, Phase 2 signs off. Failed files get a reason; the editor is notified and can re-submit.',
    accent: 'from-green-400 to-green-600',
  },
  {
    Icon: FolderTree,
    title: 'Approved files land in the Library',
    body:
      'Tagged, foldered, versioned. Smart Collections surface them automatically by vertical, editor, status, or tag.',
    accent: 'from-purple-400 to-purple-600',
  },
  {
    Icon: BarChart3,
    title: 'Buyer pulls and tracks',
    body:
      'Buyers download with codes (one code per file per buyer), launch the creative, and Orbit tracks performance via the Facebook + RedTrack metrics panel.',
    accent: 'from-pink-400 to-pink-600',
  },
];

export function HomeWorkflow() {
  return (
    <section className="relative py-24 md:py-32 bg-[#0B1220] overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(249,115,22,0.08),_transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold mb-4">
            The lifecycle of a creative
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            From brief to launch <br className="hidden md:inline" />
            <span className="text-white/50">in five moves.</span>
          </h2>
          <p className="mt-5 text-lg text-white/60 leading-relaxed">
            Every asset in Orbit goes through the same path. Here's what it looks like.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Vertical connector line (mobile) / horizontal hint (desktop spacing handled by grid) */}
          <div className="absolute left-7 top-0 bottom-0 w-px bg-gradient-to-b from-orange-500/40 via-white/10 to-transparent md:hidden" />

          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-5 md:gap-4">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: 'easeOut' }}
                className="relative"
              >
                <div className="flex items-start gap-4 md:flex-col md:items-stretch md:gap-3">
                  {/* Icon tile */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.accent} flex items-center justify-center shadow-lg`}
                    >
                      <step.Icon size={22} className="text-white" />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex-1 md:mt-2">
                    <h3 className="text-lg font-semibold text-white mb-2 leading-snug">
                      {step.title}
                    </h3>
                    <p className="text-sm text-white/60 leading-relaxed">{step.body}</p>
                  </div>
                </div>

                {/* Desktop step connector */}
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-7 left-[calc(100%-1rem)] w-8 h-px bg-gradient-to-r from-white/30 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
